import React from 'react';

import { Body } from '@depop/web-thrift-ui/Typography';
import { VideoBasicBoldIcon } from '@depop/web-ui-kit/Icons/VideoBasicBoldIcon';
import { LazyLoadImage } from '@depop/web-ui-kit/LazyLoadImage';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import { Price } from '@/components/Price';
import {
  ProductCard,
  type Props as ProductCardProps,
} from '@/components/ProductCard';
import { ProductCardLike } from '@/components/ProductCard/ProductCardLike';
import { SecondaryPriceLine } from '@/components/SecondaryPriceLine';
import { type CurrencyCode } from '@/modules/currencies/constants';
import { buildProductImageBasicProps } from '@/modules/pictures/helpers';
import { type DisplayPrice } from '@/modules/price/aliases';
import {
  type Preview,
  type SearchListProduct,
} from '@/modules/product/types.ts';

type Props = ProductCardProps & {
  pictures: Preview[] | undefined;
  displayPrice: DisplayPrice;
  hasVideo: boolean;
  size?: string | null;
  id?: SearchListProduct['id'];
  isLiked?: SearchListProduct['is_liked'];
  isBoosted?: SearchListProduct['is_boosted'];
  brandName?: SearchListProduct['brand_name'];
};

export function HomepageProductCard({
  pictures,
  hasVideo,
  size,
  id,
  isLiked,
  isBoosted,
  displayPrice,
  brandName,
  ...props
}: Props) {
  const tCommon = useTranslations('common');
  const hasMoreThanOnePicture = Boolean(pictures && pictures.length > 1);

  const conditionalProps: Partial<ProductCardProps> = {};
  if (hasMoreThanOnePicture && pictures?.[1]) {
    conditionalProps.HoverOverlay = () => (
      <LazyLoadImage alt="" {...buildProductImageBasicProps(pictures[1])} />
    );
  }

  return (
    <ProductCard
      ProductAttributes={() => (
        <div className={styles.productAttributesContainer}>
          <div className={styles.productAttributes}>
            {size && (
              <Body variant="sm-regular" aria-label={tCommon('Size')}>
                {size}
              </Body>
            )}
            <Price
              currencyCode={
                displayPrice.headline_price.currency as CurrencyCode
              }
              discountedPrice={
                displayPrice.strikethrough_price
                  ? displayPrice.headline_price.amount
                  : undefined
              }
              price={
                displayPrice.strikethrough_price?.amount ??
                displayPrice.headline_price.amount
              }
            />
            {displayPrice.secondary_price && (
              <SecondaryPriceLine
                secondaryPrice={displayPrice.secondary_price}
              />
            )}
            {brandName && <Body variant="sm-regular">{brandName}</Body>}
          </div>
        </div>
      )}
      Overlay={() => (
        <>
          {id && (
            <ProductCardLike
              productId={id}
              isLiked={isLiked}
              isBoosted={isBoosted}
            />
          )}
          {hasVideo && (
            <div className={styles.videoIcon}>
              <VideoBasicBoldIcon />
            </div>
          )}
        </>
      )}
      {...props}
      {...conditionalProps}
    />
  );
}
